import { validateWorkdayId } from './validationUtils';

export const validateEmail = (email: string): string | null => {
    if (!email.trim()) {
        return "Email is required";
    }
    if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
        return "Please enter a valid email";
    }
    return null;
};

export const validatePassword = (pwd: string, firstName: string, lastName: string): string | null => {
    if (pwd.length < 8) {
        return "Password must be at least 8 characters";
    }

    const nameParts = [firstName.toLowerCase(), lastName.toLowerCase()];
    const pwdLower = pwd.toLowerCase();
    for (const part of nameParts) {
        if (part && pwdLower.includes(part)) {
            return "Password cannot contain your name";
        }
    }

    const weakPasswords = ['password123', 'ace12345', 'password', '12345678'];
    if (weakPasswords.includes(pwdLower)) {
        return "This password is too weak or commonly used";
    }

    const hasUppercase = /[A-Z]/.test(pwd);
    const hasLowercase = /[a-z]/.test(pwd);
    const hasDigit = /\d/.test(pwd);
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd);

    const categoriesCount = [hasUppercase, hasLowercase, hasDigit, hasSpecial]
        .filter(Boolean).length;

    if (categoriesCount < 3) {
        return "Password must contain at least 3 of: uppercase, lowercase, numbers, special characters";
    }

    return null;
};

export const validatePhoneNumber = (phone: string, notificationPreferences: string[]): string | null => {
    if (!notificationPreferences.includes("text")) {
        return null;
    }

    if (!phone.trim()) {
        return "Phone number is required";
    }

    if (phone.replaceAll(/\D/g, "").length < 10) {
        return "Please enter a complete 10-digit phone number";
    }

    return null;
};

export const createStep1Validator = (
    email: string,
    workdayId: string,
    firstName: string,
    lastName: string,
    site: string,
    isSuperUser: boolean,
    notificationPreferences: string[]
) => {
    return (): { isValid: boolean; errors: Record<string, string> } => {
        const errors: Record<string, string> = {};

        const emailError = validateEmail(email);
        if (emailError) errors.email = emailError;

        const workdayIdError = validateWorkdayId(workdayId);
        if (workdayIdError) errors.workdayId = workdayIdError;

        if (!firstName.trim()) errors.firstName = "First name is required";
        if (!lastName.trim()) errors.lastName = "Last name is required";
        if (!isSuperUser && !site) errors.site = "Please select a site";
        if (notificationPreferences.length === 0) errors.notifications = "Please select at least one notification method";

        return { isValid: Object.keys(errors).length === 0, errors };
    };
};

export const createStep2Validator = (
    phone: string,
    notificationPreferences: string[],
    selectedSkills: string[],
    team: string,
    password: string,
    confirmPassword: string,
    agreedToTerms: boolean,
    lookedAtTerms: boolean,
    firstName: string,
    lastName: string,
    isAdmin: boolean,
    isSuperUser: boolean
) => {
    return (): { isValid: boolean; errors: Record<string, string> } => {
        const errors: Record<string, string> = {};

        const phoneError = validatePhoneNumber(phone, notificationPreferences);
        if (phoneError) errors.phone = phoneError;

        if (!isAdmin && !isSuperUser) {
            if (selectedSkills.length === 0) {
                errors.skills = "Please select at least one skill";
            }
            if (!team) {
                errors.team = "Please select a team";
            }
        }

        if (password.trim()) {
            const passwordError = validatePassword(password, firstName, lastName);
            if (passwordError) errors.password = passwordError;
        } else {
            errors.password = "Password is required";
        }

        if (!confirmPassword.trim()) {
            errors.confirmPassword = "Please re-enter your password";
        } else if (password !== confirmPassword) {
            errors.confirmPassword = "Passwords do not match";
        }

        if (!agreedToTerms) {
            errors.terms = "You must agree to the terms";
        }

        if (!lookedAtTerms) {
            errors.viewTerms = "You must view the terms";
        }

        return { isValid: Object.keys(errors).length === 0, errors };
    };
};